import AssignmentTurnedIn from "@mui/icons-material/AssignmentTurnedIn";
import Delete from "@mui/icons-material/Delete";
import Download from "@mui/icons-material/Download";
import Edit from "@mui/icons-material/Edit";
import Refresh from "@mui/icons-material/Refresh";
import { GridActionsCellItem, GridActionsCellItemProps } from "@mui/x-data-grid-premium";
import { ExtractionStatus, TierIiReportEncampStatus } from "generated-graphql/graphql";
import { ReactElement } from "react";
import { canGeneratePermitRenewalForDocument } from "util/permitRenewal";

import { Row } from ".";
import { canModifyLinkedSds } from "./canModifyLinkedSds";
import { buildLinkedSdsAction } from "./linkedSdsAction";

type GetDocumentsGridRowActionsParams = {
  row: Row;
  canWriteDocument: boolean;
  canGeneratePermitRenewals: boolean;
  hasDocumentAI: boolean;
  retryLoading: boolean;
  generatePermitRenewalsLoading: boolean;
  openEditDialog: (row: Row) => void;
  fetchAndHandleDownload: (args: { documentId: string }) => Promise<void>;
  handleRetryDocumentExtractions: (row: Row) => Promise<void>;
  handleGeneratePermitRenewal: (row: Row) => Promise<void>;
  onRemoveDocument: (row: Row) => void;
};

export function getDocumentsGridRowActions({
  row,
  canWriteDocument,
  canGeneratePermitRenewals,
  hasDocumentAI,
  retryLoading,
  generatePermitRenewalsLoading,
  openEditDialog,
  fetchAndHandleDownload,
  handleRetryDocumentExtractions,
  handleGeneratePermitRenewal,
  onRemoveDocument,
}: GetDocumentsGridRowActionsParams): ReactElement<GridActionsCellItemProps>[] {
  const actions: ReactElement<GridActionsCellItemProps>[] = [];
  const showGeneratePermitRenewal =
    canGeneratePermitRenewals && canGeneratePermitRenewalForDocument(row);
  const canModify = canModifyLinkedSds(row);

  if (canWriteDocument) {
    actions.push(
      buildLinkedSdsAction({
        key: "edit-document",
        label: "Edit Document",
        Icon: Edit,
        canModify,
        showInMenu: false,
        disabled: row.extractionStatus === ExtractionStatus.Pending,
        onClick: () => openEditDialog(row),
      })
    );
  }

  actions.push(
    <GridActionsCellItem
      key="download-document"
      onClick={async () =>
        await fetchAndHandleDownload({
          documentId: row.id,
        })
      }
      label="Download Document"
      icon={<Download />}
      disabled={row.extractionStatus === ExtractionStatus.Pending}
    />
  );

  if (canWriteDocument && hasDocumentAI) {
    actions.push(
      buildLinkedSdsAction({
        key: "retry-extraction",
        label: "Retry Extraction",
        Icon: Refresh,
        canModify,
        showInMenu: true,
        disabled:
          row.extractionStatus === ExtractionStatus.Pending || retryLoading || !row.tenantId,
        onClick: () => void handleRetryDocumentExtractions(row),
      })
    );
  }

  if (showGeneratePermitRenewal) {
    actions.push(
      <GridActionsCellItem
        key="generate-permit-renewal"
        showInMenu={true}
        onClick={async () => {
          await handleGeneratePermitRenewal(row);
        }}
        label="Generate Permit Renewal"
        icon={<AssignmentTurnedIn />}
        disabled={generatePermitRenewalsLoading}
      />
    );
  }

  if (
    canWriteDocument &&
    (row.report == null || row.report.encampStatus !== TierIiReportEncampStatus.FilingComplete)
  ) {
    actions.push(
      buildLinkedSdsAction({
        key: "remove-document",
        label: "Remove Document",
        Icon: Delete,
        canModify,
        showInMenu: hasDocumentAI || showGeneratePermitRenewal,
        disabled: row.extractionStatus === ExtractionStatus.Pending,
        onClick: () => onRemoveDocument(row),
      })
    );
  }

  return actions;
}
